import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SkinTypeStep from './SkinTypeStep';
import SkinToneStep from './SkinToneStep';
import SkinConcernsStep from './SkinConcernsStep';
import LifestyleStep from './LifestyleStep';
import KnownBrandsStep from './KnownBrandsStep';
import FinalReview from './FinalReview';
import FullNameStep from './FullNameStep';
import AgeStep from './AgeStep';
import GenderStep from './GenderStep';
import CityStep from './CityStep';
import JobStep from './JobStep';

import BottomNavigation from '../components/BottomNavigation';
import LoadingOverlay from '../components/LoadingOverlay';

// Worker URL
const WORKER_URL = 'https://empty-rain-2654.er-hooman.workers.dev';

const Onboarding = () => {
  const navigate = useNavigate();
  const { token, logout, refreshProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  // function to go to previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  // Rename isLoading to avoid conflict with potential loading state for saving
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [isSaving, setIsSaving] = useState(false); // Loading state for final save
  const [error, setError] = useState(null); // Error state for fetch/save
  const [answers, setAnswers] = useState({
    fullName: '',
    age: '',
    gender: '',
    city: '',
    job: '',

    skinType: [],
    skinTone: '',
    concerns: [],
    lifestyle: {
      exercise: '',
      smoking: '',
      stress: '',
      sleep: '',
      doctor: '',
      diet: ''
    },
    knownBrands: {
      iranian: [],
      international: []
    }
  });

  // Fetch existing profile data on mount
  useEffect(() => {
    const fetchInitialProfile = async () => {
      setIsFetchingInitialData(true);
      setError(null);

      if (!token) {
          console.error("Onboarding: No token found.");
          setError("برای ادامه باید وارد شوید.");
          setIsFetchingInitialData(false);
          navigate('/login');
          return;
      }

      try {
          const response = await fetch(`${WORKER_URL}/api/users/me/profile`, {
              headers: { 'Authorization': `Bearer ${token}` },
          });

          if (response.ok) {
              const userData = await response.json();
              if (userData.profileCompleted) {
                  navigate('/home');
                  // No need to set answers if redirecting immediately
                  return; 
              } else {
                  console.log('Onboarding: Profile not complete or not found. Pre-filling answers.');
                  // Pre-fill answers only if profile exists but is incomplete
                  // If profile doesn't exist (404), userData might be empty or error object
                  // Check if userData has expected keys before setting state
                  if (Object.keys(userData).length > 0) {
                       setAnswers(prevAnswers => ({
                        ...prevAnswers,
                        ...userData,
                        // Ensure nested objects are handled correctly
                        lifestyle: {
                          ...prevAnswers.lifestyle,
                          ...(userData.lifestyle || {}),
                        },
                        knownBrands: {
                          ...prevAnswers.knownBrands,
                          ...(userData.knownBrands || {}),
                        },
                        // Ensure arrays are handled correctly
                        concerns: userData.concerns || prevAnswers.concerns || [],
                      }));
                  } else {
                      console.log('Onboarding: Starting fresh, no existing data to pre-fill.');
                  }
              }
          } else if (response.status === 401) {
              console.error("Onboarding: Invalid token.");
              setError("نشست شما معتبر نیست. لطفا دوباره وارد شوید.");
              localStorage.removeItem('firebaseIdToken');
              navigate('/login');
          } else if (response.status === 404) {
               console.log('Onboarding: No existing user profile found (404). Starting fresh.');
               // No need to set error, just start with empty answers
          } else {
              const errorData = await response.json().catch(() => ({}));
              console.error("Onboarding: Error fetching profile:", response.status, errorData.error);
              setError(errorData.error || "خطا در دریافت اطلاعات اولیه.");
              // Decide how to proceed - maybe allow onboarding anyway?
          }

      } catch (err) {
          console.error("Onboarding: Network error fetching profile:", err);
          setError("خطا در ارتباط با سرور.");
      } finally {
          setIsFetchingInitialData(false);
      }
    };

    fetchInitialProfile();
    // Old Firebase listener removed
    /*
    const unsubscribe = onAuthStateChanged(auth, async (user) => { ... });
    return () => unsubscribe();
    */
   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]); // Only navigate needed, token is accessed inside the effect

  const handleNext = async () => {
    if (currentStep < 10) {
      setCurrentStep(prev => prev + 1);
    } else {
      // --- Save data using Worker --- 
      setIsSaving(true);
      setError(null);

      if (!token) {
         console.error("Onboarding Save: No token found.");
         alert("خطای احراز هویت. لطفا دوباره وارد شوید.");
         setIsSaving(false);
         navigate('/login');
         return;
      }

      // Create the user profile data payload
      const finalUserData = {
        ...answers, // Include all collected answers
        profileCompleted: true,
        // Optionally add/update a timestamp on the server-side (worker)
        // createdAt: serverTimestamp() // This cannot be sent directly
      };

      try {
         // TODO: Create this endpoint in the worker
         // It should verify token, get user ID, and save/update the data in Firestore /users/{userId}
         const response = await fetch(`${WORKER_URL}/api/users/me/profile`, {
             method: 'PUT', // Or POST if creating for the first time always
             headers: {
                 'Authorization': `Bearer ${token}`,
                 'Content-Type': 'application/json',
             },
             body: JSON.stringify(finalUserData),
         });

         if (response.ok) {
             console.log("Onboarding: Profile saved successfully via worker.");
             // Refresh the profile in AuthContext
             await refreshProfile();
             
             // Track onboarding completion event
             if (window.trackOnboardingCompletion) {
                 window.trackOnboardingCompletion();
             }
             
             navigate('/home'); // Navigate to home page after successful save
         } else if (response.status === 401) {
             console.error("Onboarding Save: Invalid token.");
             alert("نشست شما معتبر نیست. لطفا دوباره وارد شوید.");
             logout();
             navigate('/login');
         } else {
             const errorData = await response.json().catch(() => ({}));
             console.error("Onboarding Save: Error saving profile:", response.status, errorData.error);
             alert(errorData.error || 'خطا در ذخیره اطلاعات پروفایل. لطفا دوباره تلاش کنید.');
         }

      } catch (error) {
        console.error('Onboarding Save: Network error saving profile:', error);
        alert('خطا در ارتباط با سرور برای ذخیره پروفایل.');
      } finally {
          setIsSaving(false);
      }

      // --- Old Firestore save logic removed --- 
      /*
      try {
        const user = auth.currentUser;
        if (user) {
          await setDoc(doc(db, 'users', user.uid), userData);
          navigate('/home');
        }
      } catch (error) {
        console.error('Error saving profile:', error);
        alert('خطا در ذخیره اطلاعات پروفایل. لطفا دوباره تلاش کنید.');
      }
      */
    }
  };

  const steps = [
    <FullNameStep key="fullName" answers={answers} setAnswers={setAnswers} />,
    <AgeStep key="age" answers={answers} setAnswers={setAnswers} />,
    <GenderStep key="gender" answers={answers} setAnswers={setAnswers} />,
    <CityStep key="city" answers={answers} setAnswers={setAnswers} />,
    <JobStep key="job" answers={answers} setAnswers={setAnswers} />,

    <SkinTypeStep key="skinType" answers={answers} setAnswers={setAnswers} />,
    <SkinToneStep key="skinTone" answers={answers} setAnswers={setAnswers} />,
    <SkinConcernsStep key="concerns" answers={answers} setAnswers={setAnswers} />,
    <LifestyleStep key="lifestyle" answers={answers} setAnswers={setAnswers} />,
    <KnownBrandsStep key="brands" answers={answers} setAnswers={setAnswers} />,
    <FinalReview key="review" answers={answers} />
  ];

  if (isFetchingInitialData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-black">در حال بارگذاری اطلاعات آنبوردینگ...</p>
      </div>
    );
  }
  
  if (error) { // Display error if fetching initial data failed significantly
      return (
          <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4" dir="rtl">
               <div className="text-red-600 bg-red-100 p-4 rounded-md text-center">
                  <p>خطا: {error}</p>
                  <button
                      onClick={() => navigate('/login')}
                      className="mt-4 bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded"
                  >
                      بازگشت به ورود
                  </button>
              </div>
          </div>
      );
  }

  return (
    <div className="bg-white min-h-screen text-black font-light pb-8" dir="rtl">
      <LoadingOverlay 
        isLoading={isFetchingInitialData || isSaving} 
        message={
          isFetchingInitialData ? 'در حال بارگذاری اطلاعات...' :
          isSaving ? 'در حال ذخیره پروفایل...' :
          'در حال بارگذاری...'
        }
      />
      <div className="min-h-screen bg-white p-4 flex flex-col items-center justify-center">
        <div className="w-full max-w-md bg-white rounded-xl shadow-md p-6">
          {/* Progress Indicator */}
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-black h-2.5"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
            <div className="text-sm text-gray-600 text-right mt-1">
              {`مرحله ${currentStep + 1} از ${steps.length}`}
            </div>
          </div>
          {steps[currentStep]}
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 space-x-4">
            {currentStep > 0 ? (
              <button
                onClick={handlePrevious}
                disabled={isSaving}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-full hover:bg-gray-300 transition ease-in-out duration-200 disabled:opacity-50"
              >
                بازگشت
              </button>
            ) : (
              <div className="flex-1" />
            )}
            <button
              onClick={handleNext}
              disabled={isSaving}
              className={`flex-1 text-white py-3 rounded-full transition-all duration-300 transform hover:scale-105 ${{
                true: 'bg-gray-400 cursor-not-allowed',
                false: 'bg-black hover:bg-gray-800'
              }[isSaving]}`}
            >
              {isSaving ? (
                <svg className="animate-spin mx-auto h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                currentStep === steps.length - 1 ? 'پایان و ذخیره' : 'بعدی'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default Onboarding; 